// when saving the editPartForm - persist the form values to workOrderFormValues.parts
import { WithTranslation, withTranslation } from 'react-i18next';

import { connect } from 'react-redux';
import {
    clearSelectedInstallBasePartID,
    saveInstallBasePart,
    setPartFormValues,
    setSelectedInstallBasePartID,
    updatePartFormValue
} from '../../actions/partsActions';
import { IinitialState } from '../../reducers';
import { selectIsLoading } from '../../reducers/commonReducers';
import PartForm from './PartForm';

import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import { constants } from '../../constants/constants';
import { Ihistory, Ioption, IworkOrderPart } from '../../models';
import { isJobEditable } from '../../utils/reviewFlow';
import { getIsMobile } from '../../reducers/configReducer';
import { initialInstallBasePartPopulated } from '../../reducers/initialState';
import { makeSelectInstallBasesPopulatedTest } from '../../reducers/manageInventoryReducer';
import { selectIsMPFormLocked } from '../../reducers/commonSelectors';
import {
    selectAllInstallBasePartsForInstallBaseAndJob,
    selectInstallBasePartsPopulated,
    selectJobPartsForSelectedJob,
    selectPartOptions,
    selectPartStocksForUser
} from '../../reducers/partsReducer';

interface Iprops {
    colorButton?: string;
    disabled?: boolean;
    installID: string;
    history: Ihistory;
}

const mapStateToProps = (state: IinitialState, ownProps: Iprops & WithTranslation) => {
    const { selectedInstallBasePartID } = state.parts;
    const installBasePartsPopulated = selectInstallBasePartsPopulated(state);
    const selectedInstallBasePartPopulated =
        installBasePartsPopulated[selectedInstallBasePartID] || initialInstallBasePartPopulated;

    const workOrderID = state.workOrder.selectedWorkOrderID;
    let partOptions = selectPartOptions(state);

    const selectedJobID = state.manageJob.selectedJob.id;
    const allInstallBaseParts = selectAllInstallBasePartsForInstallBaseAndJob(state, {
        installBaseID: ownProps.installID
    });

    // For dropdown component: Only filter by work order parts (dropdown doesn't care about install base parts)
    // Install base parts are shown in the grid, work order parts are shown in the dropdown
    const tableParts = allInstallBaseParts;

    // changed selector to get enough information to determine virtual install bases
    const installBases = makeSelectInstallBasesPopulatedTest(state);

    // Need to grab all WorkOrderParts for the current Work Order, then tack those onto the partOptions array (could probably rework this into the selector)
    let woParts: IworkOrderPart[] = [];
    let woPartsForVirtualInstallBases: IworkOrderPart[] = [];

    const selectedJob = state.manageJob.selectedJob;

    // Let's handle Work order parts now, work order parts can only be used while the job is actively
    // being worked (New/InProgress); if it's been closed and reopened, you cannot
    // TODO move this logic to the selectPartOptions selector?
    if (selectedJob && isJobEditable(selectedJob)) {
        const jobWorkOrders = Object.values(state.manageJob.jobWorkOrdersByID).filter(x => x.jobID === selectedJobID);

        // Check if this job has more than 1 work order
        const otherJobWorkOrders = jobWorkOrders.filter(x => x.workOrderID !== workOrderID);

        if (otherJobWorkOrders && otherJobWorkOrders.length > 0) {
            // Ok now check if the other work orders have any virtual install bases
            otherJobWorkOrders.forEach(jwo => {
                // Grab the work order from the JobWorkOrder
                const workOrder = state.workOrder.workOrdersByID[jwo.workOrderID];

                if (workOrder) {
                    // Now grab the Install base and check if it's virtual
                    const ib = installBases.find(x => x.id === workOrder.installBaseID);
                    const ibIsVirtual = ib?.product?.subcategory?.id === constants.virtualProductSubcategoryID;

                    if (ibIsVirtual && workOrder.parts && workOrder.parts.length > 0) {
                        woPartsForVirtualInstallBases = [...woPartsForVirtualInstallBases, ...workOrder.parts];
                    } else if (ibIsVirtual) {
                        // If the Work Order doesn't have work order parts attached to it, double check the workOrderPartsByID
                        // This seems to be necessary if you're logged in, and recieve a new SAP WO with parts, all the pieces do not connect without doing a sync, trying to prevent the need for that
                        const woPartsForWorkOrder = Object.values(state.parts.workOrderPartsByID).filter(
                            x => x.workOrderID === workOrder.id && x.isDeleted === false
                        );
                        if (woPartsForWorkOrder && woPartsForWorkOrder.length > 0) {
                            woPartsForVirtualInstallBases = [...woPartsForVirtualInstallBases, ...woPartsForWorkOrder];
                        }
                    }
                }
            });
        }

        map(state.parts.workOrderPartsByID, woPart => {
            const wo = state.workOrder.workOrdersByID[woPart.workOrderID];
            if (wo) {
                if (woPart.workOrderID === workOrderID) {
                    woParts.push(woPart);
                }
            }
        });

        jobWorkOrders.forEach(jwo => {
            if (jwo.workOrder?.installBaseID === ownProps.installID && jwo.workOrder?.parts?.length) {
                jwo.workOrder.parts.forEach(woPart => {
                    const part = state.parts.partsByID[woPart.materialID];

                    // Simplified rule: Only check if the work order part is not deleted
                    if (part && woPart.isDeleted === false) {
                        const partOption = {
                            value: woPart.id,
                            label: `(${woPart.estimated}) ${part.number} - ${part.description} - [work order part]: ${jwo.workOrder?.number}`
                        };
                        partOptions = [...partOptions, partOption];
                    }
                });
            }
        });

        // Add virtual install base work order parts to dropdown options
        if (woPartsForVirtualInstallBases && woPartsForVirtualInstallBases.length > 0) {
            woParts = [...woParts, ...woPartsForVirtualInstallBases];

            woPartsForVirtualInstallBases.forEach(woPart => {
                const part = state.parts.partsByID[woPart.materialID];
                const workOrder = jobWorkOrders.find(jwo => jwo.workOrderID === woPart.workOrderID)?.workOrder;

                // Simplified rule: Only check if the work order part is not deleted
                if (part && woPart.isDeleted === false && workOrder) {
                    const partOption: Ioption = {
                        value: woPart.id,
                        label: `(${woPart.estimated}) ${part.number} - ${part.description} - [work order part]: ${workOrder?.number}`
                    };
                    partOptions = [...partOptions, partOption];
                }
            });
        }
    }

    // Filter out work order parts from dropdown options
    // Work order parts can still be edited by clicking on them in the grid,
    // but they won't appear in the dropdown for new selections
    partOptions = partOptions.filter(option => !option.label.includes('[work order part]'));

    // Sort dropdown options: job parts, then boot stock, all alphabetically by description
    partOptions.sort((a, b) => {
        // Determine part type priority (lower number = higher priority)
        const getTypePriority = (label: string) => {
            if (label.includes('[boot stock]')) return 2;
            return 1; // job parts (no special suffix in label)
        };

        const aPriority = getTypePriority(a.label);
        const bPriority = getTypePriority(b.label);

        // First sort by type priority
        if (aPriority !== bPriority) {
            return aPriority - bPriority;
        }

        // Extract part description for sorting (remove quantity prefix and type suffix)
        const extractDescription = (label: string) => {
            // Remove quantity prefix like "(4) "
            let cleanLabel = label.replace(/^\(\d+\)\s*/, '');
            // Remove type suffixes like " - [boot stock]"
            cleanLabel = cleanLabel.replace(/\s*-\s*\[boot stock\]$/, '');
            return cleanLabel;
        };

        const aDescription = extractDescription(a.label);
        const bDescription = extractDescription(b.label);

        // Then sort alphabetically by description/name
        return aDescription.localeCompare(bDescription);
    });

    const woPartsByID = keyBy(woParts, 'id');
    const isUserUSA = constants.usCountryID === state.user.countryID;
    const disabled = selectIsMPFormLocked(state);
    const selectedParts = allInstallBaseParts;

    return {
        partOptions,
        formValues: state.parts.partFormValues,
        installBaseID: ownProps.installID,
        loading: selectIsLoading(state),
        selectedInstallBasePartPopulated,
        selectedJobID,
        partStocks: selectPartStocksForUser(state),
        jobParts: selectJobPartsForSelectedJob(state),
        workOrderParts: woPartsByID,
        standardOptions: state.productInfo.standardOptions,
        tableParts,
        user: state.user,
        workOrderID,
        isUserUSA,
        disabled,
        isMobile: getIsMobile(state),
        selectedParts
    };
};

export default withTranslation('manageWorkOrder')(
    connect(mapStateToProps, {
        clearSelectedInstallBasePartID,
        setFormValues: setPartFormValues,
        setSelectedInstallBasePartID,
        updateFormValue: updatePartFormValue,
        saveInstallBasePart
    })(PartForm)
);
